import api from "../api/api";
import { V2_BASE_URL } from "../api/bases";

/*
 * Dashboard Refresh for practices with practiceFeatures().syncRefresh (7825):
 * v2 POST /v1/{pid}/appointments/sync in booked mode with writes, for the one
 * date the dashboard shows. It replaces OSA's v1 getfromathena call (new
 * appointments + lab/procedure tasks) and also applies Athena updates,
 * cancellations and reschedules. The server allows one run per practice and
 * date per 120 s (a click inside it gets that run's counts, rate_limited, no
 * Athena call) and a daily cap per practice (429).
 *
 * Deploy order: v2 with the sync route AND env APPT_SYNC_WRITE_PRACTICES=7825
 * first, this UI after -- otherwise every 7825 Refresh answers 403/404.
 *
 * Pinned to v2: the route exists on v2 only, so the request never goes
 * through the v1/v2 routing rules (`_gw` set, see api/api.js) and never falls
 * back to v1.
 */
export const postSyncRefresh = (practiseid, date) =>
  api.post(
    `/v1/${practiseid}/appointments/sync`,
    { mode: "booked", dry_run: false, start_date: date, end_date: date },
    { baseURL: V2_BASE_URL, _gw: "v2-pinned" },
  );

// The toast for one Refresh: { message, severity }. `res` is the response body
// (api.js returns response.data); `error` an axios error; `shownDate` the
// dashboard date ("YYYY-MM-DD").
export function syncRefreshToast(res, error, shownDate) {
  if (error) {
    const status = error.response?.status;
    const body = error.response?.data || {};
    if (!error.response) return { message: "Refresh failed: no connection to the server", severity: "error" };
    if (status === 409 && body.in_progress) return { message: "A refresh is already running", severity: "info" };
    if (status === 429) return { message: "Daily refresh limit reached", severity: "warning" };
    if (status === 403) return { message: "Refresh is not enabled for this practice", severity: "warning" };
    if (status === 400) return { message: "Refresh request not accepted (one day at a time)", severity: "warning" };
    if (status === 401) return { message: "Session expired: please log in again", severity: "warning" };
    return { message: "Refresh failed on the server", severity: "error" };
  }
  // Athena could not be reached (the server answered, but its fetch failed):
  // not a success; the server allows a retry after a short cooldown.
  if (Number(res?.refresh?.fetch_errors) > 0 || Number(res?.fetch_errors) > 0) {
    return { message: "Couldn't reach Athena — try again", severity: "warning" };
  }
  if (res?.rate_limited) {
    const last = res.last_window?.start_date;
    if (shownDate && last && last !== shownDate) {
      const n = Number(res.retry_after_seconds) || 0;
      return { message: `Try again in ${n} s`, severity: "info" };
    }
    return { message: "Refreshed just now", severity: "success" };
  }
  const r = res?.refresh || {};
  const n = (v) => Number(v) || 0;
  return {
    message: `Refreshed: ${n(r.new)} new, ${n(r.updated)} updated, ${n(r.cancelled)} cancelled`,
    severity: "success",
  };
}

// date: a dayjs or "YYYY-MM-DD" -- the date the dashboard shows.
export async function runSyncRefresh(practiseid, date) {
  const d = typeof date === "string" ? date : date.format("YYYY-MM-DD");
  try {
    return syncRefreshToast(await postSyncRefresh(practiseid, d), null, d);
  } catch (error) {
    console.error("appointments/sync refresh failed:", error?.response?.status || error?.message);
    return syncRefreshToast(null, error, d);
  }
}
